import type { ReactNode } from 'react';
import { Select } from '@/shared/ui/select';
import { verificationPolicyDescription } from '@/modules/agreements/shared';
import type { SigningVerificationPolicy } from '@/api/schemas/agreements.types';

const POLICY_OPTIONS = [
  { value: 'session_reattested', label: 'Session re-attestation' },
  { value: 'fresh_strong', label: 'Fresh identity check (passkey / security key)' },
];

/**
 * The verification-policy select.
 *
 * For a VENDOR SIGNATURE agreement the operator must choose a policy on purpose — the API
 * refuses an absent policy rather than silently defaulting. For every other shape an absent
 * value is allowed and defaults to session re-attestation.
 *
 * Lives in `shared/` because TWO surfaces ask the same question: configuring a requirement
 * against an existing version, and authoring a document's first requirement in the creation
 * stepper. A copy in each would be two places to keep the explicit-policy rule correct, and
 * the rule is the one thing here that the API enforces.
 */
export function PolicySelect(props: {
  policy: SigningVerificationPolicy | '';
  explicitRequired: boolean;
  policyChosen: boolean;
  onChange: (policy: SigningVerificationPolicy | '') => void;
}): ReactNode {
  return (
    <>
      <Select
        label="Verification policy"
        options={POLICY_OPTIONS}
        value={props.policy}
        placeholder={
          props.explicitRequired
            ? 'Choose a policy (required)'
            : 'Default: session re-attestation'
        }
        onChange={(event) =>
          props.onChange(event.target.value as SigningVerificationPolicy | '')
        }
        hint={
          props.explicitRequired
            ? 'A vendor signature agreement must state its policy explicitly. Choose fresh identity check for a mandatory vendor contract.'
            : 'Optional. An absent value defaults to session re-attestation.'
        }
        error={
          props.explicitRequired && !props.policyChosen
            ? 'A policy is required for this shape.'
            : undefined
        }
        /*
          The marker follows the SAME rule as the validation, so the label can
          never claim a field is optional while the submit refuses it. For a
          vendor signature agreement the policy is mandatory; for every other
          shape an absent value is a real, supported choice.
        */
        required={props.explicitRequired}
        optional={!props.explicitRequired}
      />
      {props.policyChosen ? (
        <p className="text-sm text-content-muted">
          {verificationPolicyDescription(props.policy as SigningVerificationPolicy)}
        </p>
      ) : null}
    </>
  );
}
